import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewContainerRef } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import * as d3 from "d3";
import { DynamicCustomTooltipService } from 'src/app/services/dynamic-custom-tooltip.service';
import { BottomGridSheetComponent } from '../../bottom-popup/bottom-grid-sheet/bottom-grid-sheet.component';

@Component({
  selector: 'app-tree-map-chart',
  templateUrl: './tree-map-chart.component.html',
  styleUrls: ['./tree-map-chart.component.scss']
})
export class TreeMapChartComponent implements OnInit, OnChanges {
  
  @Input("chartData") chartData: any;  
  @Input("chartType") chartType: any;  
  @Input("dynamicCol") dynamicCol: any;  
  @Input("colorPalette") colorPalette: any;  
  @Input("pageKey") pageKey: any;  
  @Input("item") item: any;
  @Output("detailTrigger") detailTrigger = new EventEmitter<any>();
  @Output("singleClicked") singleClicked = new EventEmitter<any>();

  @ViewChild('treeMapchart', { static: true }) treeMapchart!: ElementRef;
  @ViewChild('treeMapTooltip') treeMapTooltip!: ElementRef | any;
  private resizeObserver!: ResizeObserver;
  
  flatData:any;
  processedData: any[] = [];
  props: any = {
    chartHeight: 500,
    divId: 'treeMapDiv',
    MapGap: 5,
    multiSelectMode:false,
    cellClick:true,
    animation:true,
    firstTimeAnimation: true,
    colorPalette: {
      FAIL: '#FC5A76',
      WARNING: '#F4A744',
      ERROR: '#F4D35E'
    },
    group_key: ['business_table_name', 'result_typ'],
    today_count: 'today_count',
    previous_count: 'prev_day_count',
    leaf_key: "dqc_col_nm",
  };
  tooltipData:any;
  waitOnLoads = true;
  spikeColor:boolean=true
  
  constructor(
    private viewContainerRef: ViewContainerRef,
    private dynamicCustomTooltipService: DynamicCustomTooltipService,
    private bottomSheet: MatBottomSheet
  ) {}
  

  ngOnInit(): void {
    this.spikeColor=this.item?.config?.['spikeColor']||true
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chartData'] || changes['colorPalette'] || changes['dynamicCol']) {
      this.props = {
        ...(this.props || {}),       // ensure props always exists
        ...(this.dynamicCol || {}),  // merge dynamic columns
        colorPalette: this.colorPalette
      };

      if (changes['chartData']?.currentValue) {
        this.flatData = changes['chartData'].currentValue;
        this.processedData =  this.props.group_typ=='squence' ? 
        this.transformSequential(changes['chartData'].currentValue) : this.chartType === 'group' ? 
        this.transformToHierarchy(changes['chartData'].currentValue) : 
        this.transformData(changes['chartData'].currentValue);
        // console.log(this.processedData);
      }

      if(changes['dynamicCol']) {
        if(this.props.group_typ=='squence') {
          this.processedData = this.transformSequential(this.flatData)  
        } else { 
          this.processedData = this.chartType === 'group' ? 
          this.transformToHierarchy(this.flatData) : 
          this.transformData(this.flatData);
        }
      }

      // console.log(this.processedData)
      this.waitOnLoads = false;
      setTimeout(() => {
        if(this.processedData && this.processedData.length>0) {
          this.plotTreeMap()
        }
      }, 100);
    }
  }

  ngAfterViewInit(): void {
    this.initResizeObserver();
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private initResizeObserver(): void {
    this.resizeObserver = new ResizeObserver(() => {
      d3.select(`#${this.props.divId}`).selectAll('*').remove();
      if(!this.waitOnLoads) {
        this.plotTreeMap();
      }
    });
    this.resizeObserver.observe(this.treeMapchart.nativeElement);
  }

  private transformSequential(rawData: any[]): any[] {
    const tableKey = this.props.group_key[0];
    const todayKey = this.props.today_count;
    const prevKey = this.props.previous_count;
    const leafKey = this.props.leaf_key;
    const statusKey = this.props.status_key;
    const errorKey = this.props.error_key || 'error_type_code';
    const STATUS_ORDER = ["FAIL", "ERROR", "WARNING"];
    if (!rawData?.length) return [];

    const statusSort = (a: any, b: any) => STATUS_ORDER.indexOf(a[statusKey]) - STATUS_ORDER.indexOf(b[statusKey]);
    const raDa = rawData.sort(statusSort);

    // STEP 1: Sort rows by Today Count DESC
    const sorted = raDa.slice().sort(
        (a, b) => (b[todayKey] ?? 0) - (a[todayKey] ?? 0)
    );

    // STEP 2: Group continuous rows if same TABLE NAME
    const continuousGroups: any[][] = [];
    let currentGroup: any[] = [sorted[0]];

    for (let i = 1; i < sorted.length; i++) {
        const prevTable = sorted[i - 1][tableKey];
        const currTable = sorted[i][tableKey];

        if (currTable === prevTable) {
            // Same table → same group
            currentGroup.push(sorted[i]);
        } else {
            // New table → start new group
            continuousGroups.push(currentGroup);
            currentGroup = [sorted[i]];
        }
    }

    continuousGroups.push(currentGroup);

    // STEP 3: Convert each group into your existing tree format
    const finalResult: any[] = [];

    continuousGroups.forEach(groupRows => {
        const tableName = groupRows[0][tableKey];

        const leafMap: Record<string, { today: number; prev: number; status: string; error: string }> = {};

        groupRows.forEach((row: any) => {
            const leaf = row[leafKey];
            const error = row[errorKey];
            const status = row[statusKey];

            if (!leafMap[leaf]) {
                leafMap[leaf] = { today: 0, prev: 0, status, error };
            }

            leafMap[leaf].today += row[todayKey] ?? 0;
            leafMap[leaf].prev += row[prevKey] ?? 0;
        });

        const children = Object.entries(leafMap).map(([leaf, c]) => {
            const diff = c.today - c.prev;
            return {
                groupName: tableName,
                name: leaf,
                group: c.status,
                status: c.status,
                error: c.error,
                value: c.today,
                difference: diff,
                trend: diff === 0 ? "LINE" : diff > 0 ? "UP" : "DOWN"
            };
        });

        const totalToday = d3.sum(children, c => c.value);
        const totalPrev = d3.sum(children, c => c.value - c.difference);
        const diff = totalToday - totalPrev;

        finalResult.push({
            ...groupRows[0],
            groupName: tableName,
            name: tableName,
            status: groupRows[0][statusKey],
            error: groupRows[0][errorKey],
            group: groupRows[0][statusKey],
            value: totalToday,
            difference: diff,
            trend: diff === 0 ? "LINE" : diff > 0 ? "UP" : "DOWN",
            children
        });
    });

    return finalResult;
  }

  
  private transformToHierarchy(rawData: any[]): any[] {
    const groupKeys = this.props.group_key;    // dynamic: 1..N
    const todayKey = this.props.today_count;
    const prevKey = this.props.previous_count;
    const leafKey = this.props.leaf_key;
    const statusKey = this.props.status_key;
    const errorKey = this.props.error_key || 'error_type_code';
    const sortBy = this.props.sort_by;
    const STATUS_ORDER = ["FAIL", "ERROR", "WARNING"];

    if (!rawData?.length) return [];

    const sorted = rawData.slice().sort(
      (a, b) => sortBy == 'desc' ? (b[todayKey] ?? 0) - (a[todayKey] ?? 0) : ((a[todayKey] ?? 0) - (b[todayKey] ?? 0))
    );

    // Dynamic grouping for infinite levels
    const grouped = (d3.groups as any).apply(null, [
        sorted,
        ...groupKeys.map((key: any) => (d: any) => d[key])
    ]);

    const result: any[] = [];

    // -----------------------------------------------------------
    // Recursive group processor
    // -----------------------------------------------------------
    const processGroup = (groupNode: any, level: number, parentNames: string[] = []) => {
        const [groupValue, subItems] = groupNode;

        // If this is the LAST group level → subItems = row array
        if (level === groupKeys.length - 1) {
            // const groupName = [...parentNames, groupValue].join(" - ");
            const groupName:any = parentNames[0] ?? groupValue;

            const leafMap: Record<string, { today: number; prev: number; status: string; error: string }> = {};

            subItems.forEach((row: any) => {
                const leafVal = row[leafKey];
                const error = row[errorKey];
                const status = row[statusKey];     // ALWAYS use statusKey

                if (!leafMap[leafVal]) {
                    leafMap[leafVal] = { today: 0, prev: 0, status, error };
                }

                leafMap[leafVal].today += row[todayKey] ?? 0;
                leafMap[leafVal].prev += row[prevKey] ?? 0;
            });

            const children = Object.entries(leafMap).map(([leafName, counts]) => {
                const diff = counts.today - counts.prev;

                return {
                    groupName: groupName,
                    name: leafName,
                    group: counts.status,          // status is fixed
                    status: counts.status,
                    error: counts.error,
                    value: counts.today,
                    difference: diff,
                    trend: diff == 0 ? "LINE" : diff > 0 ? "UP" : "DOWN",
                };
            });

            const totalToday = d3.sum(children, (x: any) => x.value);
            const totalPrev = d3.sum(children, (x: any) => x.value - x.difference);
            const totalDiff = totalToday - totalPrev;

            // Parent status is determined by **first row's status**
            const parentStatus = subItems[0][statusKey];
            const parentErrorStatus = subItems[0][errorKey];

            result.push({
                groupName: groupName,
                name: groupName,
                status: parentStatus,             // always from data
                group: parentStatus,
                error: parentErrorStatus,
                value: totalToday,
                difference: totalDiff,
                trend: totalDiff == 0 ? "LINE" : totalDiff > 0 ? "UP" : "DOWN",
                children
            });

        } else {
            // Middle levels → loop deeper
            subItems.forEach((childGroup: any) => {
                processGroup(childGroup, level + 1, [...parentNames, groupValue]);
            });
        }
    };

    grouped.forEach((g: any) => processGroup(g, 0));

    if (this.props.sort_by === "desc") {
        result.sort((a, b) => b.value - a.value);
    }

    if (this.props.sort_by === "asc") {
        result.sort((a, b) => a.value - b.value);
    }

    if(this.props.group_typ == 'status') {
      const statusSort = (a: any, b: any) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status);
      // debugger
      return result.sort(statusSort);
    } else {
      return result;
    }
  }

  private transformData(data: any[]): any[] {
    const groupKey = this.props.group_key;

    //@ts-ignore
    const grouped = d3.groups(
      data,
      d => groupKey.map((k: any) => d[k]).join("|||")
    );

    return grouped.map(([key, items]) => {
      const [fileType, schema, cycle] = key.split("|||");
      const name = `${fileType}`;

      const total:any = d3.sum(items, d => d[this.props.today_count]);
      const prevTotal = d3.sum(items, d => d[this.props.previous_count]);
      const difference = total - prevTotal;
      const trend = difference == 0 ? 'LINE' : difference > 0 ? "UP" : "DOWN";

      // --- Aggregate items by result_typ ---
      const resultTypeMap: Record<string, { today_count: number; prev_day_count: number }> = {};

      items.forEach(item => {
        const key = item.result_typ;
        if (!resultTypeMap[key]) {
          resultTypeMap[key] = { today_count: 0, prev_day_count: 0 };
        }
        resultTypeMap[key].today_count += item[this.props.today_count] || 0;
        resultTypeMap[key].prev_day_count += item[this.props.previous_count] || 0;
      });

      const decoratedItems = Object.entries(resultTypeMap).map(([result_typ, counts]) => {
        const diff = counts.today_count - counts.prev_day_count;
        const itemTrend = diff == 0 ? 'LINE' : diff > 0 ? "UP" : "DOWN";

        return {
          result_typ,
          today_count: counts.today_count,
          prev_day_count: counts.prev_day_count,
          difference: diff,
          trend: itemTrend
        };
      });

      // Build a map of counts only for statuses with today_count > 0
      let statusCount: Record<string, number> = {};

      if (this.colorPalette) {
        Object.keys(this.colorPalette).forEach((key: string) => {
          statusCount[key] = 0;
        });
      }


      decoratedItems.forEach(item => {
        if (item.today_count > 0) {
          statusCount[item.result_typ] += 1;
        }
      });

      let priority:any = this.colorPalette
        ? Object.keys(this.colorPalette).map((key: string) => [key])
        : [];

      const status = priority.find((type:any) => statusCount[type] > 0) || null;

      return {
        ...items[0],
        name,
        value: total,
        trend,
        difference,
        status,
        items: decoratedItems
      };
    });
  }

  private plotTreeMap(): void {
    const myChart = this;
    const myClass = myChart.props.divId;

    const upIconPath = 'M9.68613 0H6.50078C6.32754 0 6.18691 0.145126 6.18691 0.324363V0.979549C6.18691 1.15858 6.32754 1.30391 6.50078 1.30391H7.84629L5.09316 4.1489L3.92598 2.94268C3.67949 2.68816 3.28008 2.68816 3.03359 2.94268L0.0919922 5.98306C-0.0306641 6.10962 -0.0306641 6.31509 0.0919922 6.44165L0.540234 6.90508C0.662695 7.03164 0.861523 7.03164 0.983984 6.90508L3.47988 4.32572L4.64687 5.53194C4.89334 5.78673 5.29281 5.78683 5.53926 5.53194L8.73828 2.22594V3.61644C8.73828 3.79547 8.87871 3.9408 9.05215 3.9408H9.68613C9.85957 3.9408 10 3.79547 10 3.61644V0.324363C10 0.145126 9.85957 0 9.68613 0Z';
    const downIconPath = 'M0.313868 7H3.49922C3.67246 7 3.81309 6.85487 3.81309 6.67564V6.02045C3.81309 5.84142 3.67246 5.69609 3.49922 5.69609H2.15371L4.90684 2.8511L6.07402 4.05732C6.32051 4.31184 6.71992 4.31184 6.96641 4.05732L9.90801 1.01694C10.0307 0.890384 10.0307 0.684907 9.90801 0.558351L9.45977 0.0949173C9.3373 -0.0316386 9.13848 -0.0316386 9.01602 0.0949173L6.52012 2.67428L5.35313 1.46806C5.10666 1.21327 4.70719 1.21317 4.46074 1.46806L1.26172 4.77406V3.38356C1.26172 3.20453 1.12129 3.0592 0.947851 3.0592H0.313868C0.140429 3.0592 0 3.20453 0 3.38356V6.67564C0 6.85487 0.140429 7 0.313868 7Z';
    const lineIconPath = 'M0.75 2.75H11.0833 M9.41406 0.75L11.4141 2.75L9.41406 4.75';
    

    const container: any = d3.select('#' + myClass);
    const width = container.node()?.getBoundingClientRect().width || 600;
    const height = myChart.props.chartHeight;

    container.selectAll('*').remove();

    // Tooltip setup
    d3.select('#tooltip_' + myClass).remove();
    const tooltip = container
      .append('div')
      .attr('id', 'tooltip_' + myClass)
      .attr('class', 'tooltip')
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('background', '#333')
      .style('color', '#fff')
      .style('padding', '6px 10px')
      .style('border-radius', '6px')
      .style('font-family', 'Poppins')
      .style('font-size', '12px')
      .style('pointer-events', 'none');

    const groupTooltip = d3.select("body")
      .append("div")
      .attr("class", "group-tooltip")
      .style("position", "absolute")
      .style("padding", "6px 10px")
      .style("background", "#000")
      .style("color", "#fff")
      .style("border-radius", "4px")
      .style("font-size", "12px")
      .attr("font-weight", "600")
      .style("font-family", "Poppins")
      .style("visibility", "hidden")
      .style("z-index", "9999999")
      .style("pointer-events", "none");

    const mySvg = d3.select(this.treeMapchart.nativeElement)
      .append('svg')
      .attr('id', 'svg_' + myClass)
      .attr('width', width)
      .attr('height', height)
      .style('background-color', myChart.chartType == 'group' ? 'rgba(19, 99, 223, 0.1)' : 'white')
      .style('border-radius', myChart.chartType == 'group' ? '5px' : '0');

    // debugger
    const maxValue = d3.max(myChart.processedData, (d: any) => d.value || 0);
    const MIN_LAYOUT_VALUE = maxValue * 0.06;   // 5% of largest value
    // or: const MIN_LAYOUT_VALUE = Math.max(1000, maxValue / 20);
    // const MIN_LAYOUT_VALUE = 1000;
    const root = d3.hierarchy({ children: myChart.processedData })
      .sum((d: any) => {
        if (d.children) return 0;
        return Math.max(d.value, MIN_LAYOUT_VALUE);
      })
      .sort((a, b) => (b.children ? 0 : (b.value || 0)) - (a.children ? 0 : (a.value || 0)));


    const treemapLayout = myChart.chartType == 'group'
    ? d3.treemap()
        .size([width, height])
        .paddingTop(15)
        .paddingRight(this.props.MapGap)
        .paddingLeft(this.props.MapGap)
        .paddingInner(this.props.MapGap)
        .paddingBottom(this.props.MapGap)
    : d3.treemap()
        .size([width, height])
        .padding(this.props.MapGap);


    treemapLayout(root);

    const nodes = mySvg
      .selectAll('g')
      .data(root.leaves())
      .join('g')
      .attr('transform', (d: any) => `translate(${d.x0},${d.y0})`);

    const color:any = d3.scaleOrdinal()
    .domain(["FAIL", "ERROR", "WARNING"])
    .range([ "#FC5A76", "#F4A744", "#F4D35E"])

    const opacity:any = d3.scaleLinear()
      .domain([1, 11716])
    .range([.8,1])

    let multiSelectMode = this.props.multiSelectMode; // switch between single/multi select
    const selectedSet = new Set();

    let hoverTimeout: any = null;   // show delay (if needed)
    let idleTimeout: any = null;    // hide after 3 sec of no movement
    let clickTimer: any;
    const rects = nodes.append('rect')
      .attr("class", "treemap-rect")
      .attr('width', (d: any) => d.x1 - d.x0)
      .attr('height', (d: any) => d.y1 - d.y0)
      .attr('fill', (d: any) => myChart.props.colorPalette[d.data.status])
      // .attr('fill', (d: any) => color(d.data.status))
      // .attr('opacity', (d: any) => opacity(d.data.value))
      .attr('rx', 4)
      .attr('ry', 4)
      .attr('stroke', 'none')
      .attr('stroke-width', 0)
      .on('mouseover', function (event: MouseEvent, d: any) {
        myChart.props.firstTimeAnimation = false;
        myChart.tooltipData = d.data;
        myChart.dynamicCustomTooltipService.createMouseTooltip(myChart.treeMapTooltip, myChart.viewContainerRef, event);
        resetIdleTimer(event);
      })
      .on('mousemove', function (event: MouseEvent) {
        // Clear any previous timer
        // clearTimeout(hoverTimeout);

        // // Hide tooltip while moving
        // // myChart.dynamicCustomTooltipService.hideMouseTooltip();

        // // Set a timer to show tooltip if mouse stops moving
        // hoverTimeout = setTimeout(() => {
        //   // myChart.dynamicCustomTooltipService.createMouseTooltip(
        //   //   myChart.treeMapTooltip,
        //   //   myChart.viewContainerRef,
        //   //   event
        //   // );
        // }, 300);
        myChart.dynamicCustomTooltipService.updateTooltipPosition(myChart.treeMapTooltip, myChart.viewContainerRef, event);
        resetIdleTimer(event);
      })
      .on('mouseout', function () {
        clearTimeout(hoverTimeout);
        clearTimeout(idleTimeout);
        myChart.dynamicCustomTooltipService.hideMouseTooltip();
      })
      .on('click', function (event: any, d: any) {
        if(!myChart.props.cellClick) return;
        
        if(myChart.chartType =='group') {
          rects.transition().duration(150).attr("fill", "#8A98AB"); // light grey

          // Restore color of clicked rect
          d3.select(this).transition().duration(150).attr("fill", myChart.props.colorPalette[d.data.status]);
        }
        let key = d.data; // or unique ID
        
        if (clickTimer) {
          clearTimeout(clickTimer);
          clickTimer = null;
        }
        // wait a little to decide if it's single or double
        clickTimer = setTimeout(() => {
          clickTimer = null;
          let selectedTiles = viewChildData(key);
          onSingleClickTile(selectedTiles);
        }, 250);
      })
      .on('dblclick', function (event: MouseEvent, d: any) {
        if(!myChart.props.cellClick) return;
        if(myChart.chartType=='group') return;
        let key = d.data; // or unique ID

        if (clickTimer) {
          clearTimeout(clickTimer);
          clickTimer = null;
        }
        let selectedTiles = viewChildData(key);
        onDoubleClickTile(selectedTiles);
      });


    // if(myChart.props.animation && myChart.props.firstTimeAnimation){
      // rects
      //   .attr('opacity', 0)
      //   .attr('transform-origin', function(d:any) {
      //     return `${(d.x1 - d.x0) / 2}px ${(d.y1 - d.y0) / 2}px`;
      //   })
      //   .attr('transform', 'scale(0.5)')
      //   .transition()
      //   .delay((d:any, i:any) => i * 100)  // stagger by 100ms per rect
      //   .duration(0)
      //   .ease(d3.easeCubicOut)
      //   .attr('opacity', 1)
      //   .attr('transform', 'scale(1)');
    // }

    const labelGroup = nodes.append('g')
      .attr('pointer-events', 'none')
      .attr('class', 'label-group');

    // Top-left value group (hide if too small)
    const valueGroup = labelGroup.append('g')
      .attr('class', 'value-group')
      .filter((d: any) => {
        const boxWidth = d.x1 - d.x0;
        const boxHeight = d.y1 - d.y0;
        return boxWidth > 50 && boxHeight > 20; // only show if big enough
      });

    valueGroup.append('text')
      .attr('x', 8)
      .attr('y', (d: any) => Math.min(16, (d.y1 - d.y0) - 2)) // Prevent overflow
      .attr('fill', 'white')
      .attr('class', 'top-left-value')
      .style('font-size', '12px')
      .style('font-weight', '600')
      .style('font-family', 'Poppins')
      .text((d: any) => d.data.value);

    
    // Top-right container group (difference + trend together)
    const topRightGroup = labelGroup.append('g')
      .attr('class', 'top-right-group')
      .filter((d: any) => {
        const boxWidth = d.x1 - d.x0;
        const boxHeight = d.y1 - d.y0;
        return boxWidth > 50 && boxHeight > 20; // hide on small boxes
      })
      .attr('transform', (d: any) => {
        const nodeWidth = d.x1 - d.x0;
        return `translate(${nodeWidth - 8}, 8)`; // anchor to top-right with padding
      });

    // Difference text (always show, even if 0)
    topRightGroup.append('text')
      .attr('class', 'difference-text')
      .attr('y', 8)
      .attr('text-anchor', 'end')
      .attr('fill', '#ffffff')
      .style('font-size', '12px')
      .style('font-weight', '600')
      .style('font-family', 'Poppins')
      .text((d: any) => `${Math.abs(d.data.difference)}`);

    // Trend icon — only if UP or DOWN
    topRightGroup.filter((d: any) => d.data.trend === 'UP' || d.data.trend === 'DOWN' || d.data.trend === 'LINE')
      .append('path')
      .attr('class', 'trend-icon')
      .attr('d', (d: any) => d.data.trend === 'LINE' ? lineIconPath : d.data.trend === 'UP' ? upIconPath : downIconPath)
      // .attr('fill', '#ffffff')
      .attr("fill", (d: any) => d.data.trend === "LINE" ? "none" : "#ffffff")
      .attr("stroke", (d: any) => d.data.trend === "LINE" ? "#fff" : "none")
      .attr("stroke-width", (d: any) => d.data.trend === "LINE" ? "1.1" : "none")
      .attr('transform', function(this: SVGPathElement) {
        const diffWidth = d3.select(this.parentNode as SVGGElement)
          .select<SVGTextElement>('.difference-text')
          .node()
          ?.getComputedTextLength() || 0;

        const gap = 6; // space between number and icon
        return `translate(${-diffWidth - gap - 10}, 0) scale(1.2)`;
      });

    // Center name with wrapping
    labelGroup.append('text')
      .attr('x', (d: any) => (d.x1 - d.x0) / 2)
      .attr('y', (d: any) => (d.y1 - d.y0) / 2)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('fill', 'white')
      .style('font-size', '13px')
      .style('font-weight', 'bold')
      .style('font-family', 'Poppins')
      .text((d: any) => d.data.name)
      .each(function (this: SVGTextElement, d: any) {
        const availableWidth = d.x1 - d.x0 - 4;
        const availableHeight = d.y1 - d.y0 - 4;

        // Wrap text to fit width
        wrap(d3.select<SVGTextElement, any>(this), availableWidth);

        // After wrapping, measure actual text block
        const bbox = this.getBBox();
        if (bbox.width + 20 > availableWidth || bbox.height + 20 > availableHeight) {
          d3.select(this).text(''); // Hide if overlaps box
        }
      });

      setTimeout(() => {
        labelGroup.each(function () {
          const g = d3.select(this);

          const valueEl = g.select(".value-group text").node();
          const rightEl = g.select(".top-right-group").node();
          if (!valueEl || !rightEl) return;

          // Convert BBox to global/screen coordinates
          function getGlobalBox(el: any) {
            const bbox = el.getBBox();
            const matrix = el.getScreenCTM();
            const corners = [
              new DOMPoint(bbox.x, bbox.y).matrixTransform(matrix),
              new DOMPoint(bbox.x + bbox.width, bbox.y).matrixTransform(matrix),
              new DOMPoint(bbox.x, bbox.y + bbox.height).matrixTransform(matrix),
              new DOMPoint(bbox.x + bbox.width, bbox.y + bbox.height).matrixTransform(matrix)
            ];

            const xs = corners.map(p => p.x);
            const ys = corners.map(p => p.y);

            return {
              x: Math.min(...xs),
              y: Math.min(...ys),
              width: Math.max(...xs) - Math.min(...xs),
              height: Math.max(...ys) - Math.min(...ys)
            };
          }

          const valueBox = getGlobalBox(valueEl);
          const rightBox = getGlobalBox(rightEl);

          // Overlap check (global coords)
          const overlap =
            valueBox.x < rightBox.x + rightBox.width &&
            valueBox.x + valueBox.width > rightBox.x &&
            valueBox.y < rightBox.y + rightBox.height &&
            valueBox.y + valueBox.height > rightBox.y;

          if (overlap) {
            g.select(".value-group").style("display", "none");
            g.select(".top-right-group").style("display", "none");
          }
        });
      }, 0);

    if(myChart.chartType == 'group') {
      const groups = root.descendants().filter((d: any) => d.depth === 1);

      const headerGroup = mySvg
        .selectAll(".header-row")
        .data(groups)
        .join("g")
        .attr("class", "header-row")
        .attr("transform", (d: any) => `translate(${d.x0 + 6}, ${d.y0 + 10})`);

      headerGroup
        .append("text")
        .attr("class", "header-left")
        .attr("x", 0)
        .attr("y", 0)
        .style("font-size", "13px")
        .style("font-weight", "700")
        .style("font-family", "Poppins")
        .style("fill", "#101D42")
        .text((d: any) => d.data.name)
        // @ts-ignore
        .each(function (this: SVGTextElement, d: any) {
            const availableWidth = d.x1 - d.x0 - 12;
          
            const textEl = d3.select(this);
            let textVal = d.data.name;

            this.textContent = textVal;

            if (availableWidth < 30) {
              textEl.text("");
              return;
            }

            while (this.getComputedTextLength() > (availableWidth - 25) && textVal.length > 0) {
              textVal = textVal.slice(0, -1);
              this.textContent = textVal + "...";
            }
        })
        .on("mouseover", function (event: MouseEvent, d: any) {
          groupTooltip.style("visibility", "visible").text(d.data.name);
        })
        .on("mousemove", function (event: MouseEvent) {
          const tooltipWidth = groupTooltip.node()?.offsetWidth || 120;
          const tooltipHeight = groupTooltip.node()?.offsetHeight || 35;
  
          const padding = 12; // distance from cursor
  
          let posX = event.clientX + padding;
          let posY = event.clientY + padding;
  
          // If tooltip goes out of right screen, shift left
          if (posX + tooltipWidth > window.innerWidth) {
            posX = event.clientX - tooltipWidth - padding;
          }
  
          // If tooltip goes out of bottom screen, shift up
          if (posY + tooltipHeight > window.innerHeight) {
            posY = event.clientY - tooltipHeight - padding;
          }
  
          groupTooltip
            .style("left", `${posX}px`)
            .style("top", `${posY}px`);
        })
        .on("mouseout", function () {
          groupTooltip.style("visibility", "hidden");
        });

      // VALUE (next to name)
      headerGroup
        .append("text")
        .attr("class", "header-value")
        .style("font-size", "13px")
        .style("font-weight", "600")
        .style("font-family", "Poppins")
        .style("fill", "#8A98AB")
        // @ts-ignore
        .attr("x", function (this: SVGTextElement, d: any) {
          const nameWidth = d3.select(this.parentNode as SVGGElement)
                .select<SVGTextElement>('.header-left')
                .node()
                ?.getComputedTextLength() || 0;
          return nameWidth + 10; // 10px spacing after name
        })
        .attr("y", 0)
        // .text((d: any) => d.data.value);
        .text(function (this: SVGTextElement, d: any) {
          const g = d3.select(this.parentNode as SVGGElement);

          const nameEl = g.select<SVGTextElement>(".header-left").node();
          const nameWidth = nameEl?.getComputedTextLength() || 0;

          const availableWidth = d.x1 - d.x0 - 12;

          const value = d.data.value.toString();

          // First assign full value to measure width
          this.textContent = value;
          const valueWidth = this.getComputedTextLength();

          const spacing = 10;
          const requiredWidth = nameWidth + spacing + valueWidth;

          // If no space → hide value
          if (requiredWidth > availableWidth) {
            this.textContent = "";
            return "";
          }

          return value;
      });

    const rightGroup = headerGroup.append('g')
      .attr('class', 'header-right')
      // @ts-ignore
      .filter(function (d: any, this: SVGTextElement) {
        const parent = d3.select(this.parentNode as SVGGElement);
        const boxWidth = d.x1 - d.x0;
        const padding = 15; // space for safety
        // Measure left side
        const nameWidth = parent.select<SVGTextElement>('.header-left').node()?.getComputedTextLength() || 0;
        const valueWidth = parent.select<SVGTextElement>(".header-value").node()?.getComputedTextLength() || 0;
        const leftTotal = nameWidth + 10 + valueWidth;
        // Estimate right-side width
        const diffText = Math.abs(d.data.difference).toString();
        // Create a temp text to measure diff width
        const temp = parent.append("text").style("font-size", "13px").text(diffText);

        const diffWidth = temp.node()?.getComputedTextLength() || 0;
        temp.remove();
        const iconWidth = d.data.trend === "LINE" ? 13 : 10;
        const rightTotal = diffWidth + iconWidth + 8;
        // Required total width
        const needed = leftTotal + rightTotal;
        // Condition 1: node must be reasonably sized
        if (boxWidth < 50 || (d.y1 - d.y0) < 20) return false;
        // Condition 2: hide if overlap happens
        return needed + padding <= boxWidth;
      })
      .attr('transform', (d: any) => {
        const nodeWidth = d.x1 - d.x0;
        return `translate(${nodeWidth - 15}, -8)`; // anchor to top-right with padding
      });

    // Difference text (always show, even if 0)
    rightGroup.append('text')
      .attr('class', 'difference-text')
      .attr('y', 8)
      .attr('text-anchor', 'end')
      .style("font-size", "13px")
      .style('font-weight', '600')
      .style('font-family', 'Poppins')
      .style("fill", "#8A98AB")
      .text((d: any) => `${Math.abs(d.data.difference)}`);

    // Trend icon — only if UP or DOWN
    rightGroup.filter((d: any) => d.data.trend === 'UP' || d.data.trend === 'DOWN' || d.data.trend === 'LINE')
      .append('path')
      .attr('class', 'trend-icon')
      .attr('d', (d: any) => d.data.trend === 'LINE' ? lineIconPath : d.data.trend === 'UP' ? upIconPath : downIconPath)
      .attr("fill", (d: any) => d.data.trend === "LINE" ? "none" : d.data.trend === "DOWN" ? "#3AC97C" : "#FF4567")
      .attr("stroke", (d: any) => d.data.trend === "LINE" ? "#fff" : "none")
      .attr("stroke-width", (d: any) => d.data.trend === "LINE" ? "1.1" : "none")
      .attr('transform', function(this: SVGPathElement) {
        const diffWidth = d3.select(this.parentNode as SVGGElement).select<SVGTextElement>('.difference-text').node()?.getComputedTextLength() || 0;
        const gap = 6; // space between number and icon
        return `translate(${-diffWidth - gap - 10}, 0) scale(1.2)`;
      })
    }

    function wrap(
      text: d3.Selection<SVGTextElement, any, any, any>,
      width: number
    ): void {
      text.selectAll('tspan').remove();
      const words = text.text().split(/\s+/).reverse();
      let word: string | undefined;
      let line: string[] = [];
      const lineHeight = 1.1; // ems
      const x = text.attr('x');
      let lineNumber = 0;
      const dy = 0;

      let tspan = text.text(null)
        .append('tspan')
        .attr('x', x)
        .attr('dy', dy + 'em');

      while ((word = words.pop())) {
        line.push(word);
        tspan.text(line.join(' '));
        if (tspan.node()!.getComputedTextLength() > width) {
          line.pop();
          tspan.text(line.join(' '));
          line = [word];
          if (word.trim().length > 0) {
            tspan = text.append('tspan')
              .attr('x', x)
              .attr('dy', ++lineNumber * lineHeight + dy + 'em')
              .text(word);
          }
        }
      }
    }

    drawLegend();

    function drawLegend(): void {
      
      const legendData = myChart?.colorPalette
        ? Object.keys(myChart.colorPalette).map((key: string) => ({
            label: key,
            color: myChart.colorPalette[key]
          }))
        : [];

      d3.select('#legend_' + myClass).remove();

      const legendWrapper = d3.select('#' + myClass)
        .insert('div', ':first-child')
        .attr('id', 'legend_' + myClass)
        .style('display', 'flex')
        .style('justify-content', 'center')
        .style('gap', '20px')
        .style('margin-bottom', '12px')
        .style('font-family', 'Poppins')
        .style('font-size', '13px');

      legendWrapper.selectAll('div')
        .data(legendData)
        .enter()
        .append('div')
        .style('display', 'flex')
        .style('align-items', 'center')
        .html((d:any) => `
          <span style="width:12px;height:12px;background:${d.color};border-radius:50%;display:inline-block;margin-right:6px;"></span>
          ${d.label}
        `);
    }

    function viewChildData(key:any) {
      // @ts-ignore
      const rect = d3.select(this).select('rect');

      if (multiSelectMode) {
        // Multi-select logic
        if (selectedSet.has(key)) {
          // Deselect
          selectedSet.delete(key);
          selectedSet.add(key);
            //@ts-ignore
          rect.select(this).attr('stroke', 'none').attr('stroke-width', 0);
        } else {
          // Select
          selectedSet.add(key);
          //@ts-ignore
          rect.select(this).attr('stroke', 'green').attr('stroke-width', 3).attr('stroke-dasharray', '4 2') ;
        }
      } else {
        // Single-select logic with toggle
        if (selectedSet.has(key)) {
          // Deselect if clicked again
          selectedSet.delete(key);
          selectedSet.add(key);
            //@ts-ignore
          rect.select(this).attr('stroke', 'none').attr('stroke-width', 0);
        } else {
          // Deselect any previously selected
          rect.selectAll('rect').attr('stroke', 'none').attr('stroke-width', 0);
          selectedSet.clear();

          // Select current
          selectedSet.add(key);
            //@ts-ignore
          rect.select(this).attr('stroke', 'green').attr('stroke-width', 3).attr('stroke-dasharray', '4 2') ;
        }
      }
      return Array.from(selectedSet)
    }

    function onSingleClickTile(event:any) {
      if(myChart.chartType =='group') {
        if(event && event?.[0]) {
          let obj = {
            file_type_dim_key: event?.[0]?.file_type_dim_key || undefined,
            db_schema_key: event?.[0]?.db_schema_key || undefined,
            dqc_col_nm: event?.[0]?.dqc_col_nm || event?.[0]?.name || undefined,
            // table_name: event[0]?.table_name || undefined
            table_name: event[0]?.business_table_name || event[0]?.groupName || undefined,
            defect_type: event[0]?.status ? event[0]?.status.toString() : undefined,
            error_type_code:
              event[0]?.error_type_code
              || event[0]?.error
              || (event[0]?.status ? event[0]?.status.toString() : undefined)

            // defect_type: event[0]?.status || undefined,
            // error_type_code: event[0]?.error_type_code || event[0]?.error || event[0]?.status || undefined,
          }
          myChart.openPopup(obj);
        }
      } else {
        myChart.singleClicked.emit(event);
      }
      
      // setTimeout(() => {
      // }, 1000);
    }
    
    function onDoubleClickTile(selectedTiles:any) {
      myChart.detailTrigger.emit(selectedTiles);
    }

    function resetIdleTimer(event: MouseEvent) {
      clearTimeout(idleTimeout);

      idleTimeout = setTimeout(() => {
        // Hide tooltip after 3 seconds of no mouse movement
        myChart.dynamicCustomTooltipService.hideMouseTooltip();
      }, 10000);
    }
  }

  openPopup(obj:any): void {
    this.bottomSheet.open(BottomGridSheetComponent, {
      data: {
        obj: obj,
        tooltipData: this.tooltipData,
        colorPalette: this.colorPalette,
        widgetInfo: this.item,
        pageKey: this.pageKey,
      },
      panelClass: 'du-custom-bottom-sheet',
      hasBackdrop: true,
      disableClose: false
    }).afterDismissed().subscribe(() => {
      // Handle after close if needed
      this.resetRectColors()
    });
  }

  resetRectColors() {
    d3.selectAll(".treemap-rect")
      .transition()
      .duration(150)
      .attr("fill", (d: any) => this.props.colorPalette[d.data.status]);
  }
}

